import MessageFormatter from 'shared/helpers/MessageFormatter';

// Markdown for assistant answers. Raw HTML is off in the formatter and the result is sanitized
// with v-dompurify-html. Images are not rendered: an image URL written by the model would be
// fetched by the browser on its own, which could leak data the model read.
export const renderAnswer = content => {
  const formatter = new MessageFormatter(content || '');
  formatter.disableImageRendering();
  return formatter.formattedMessage;
};

export const IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];
export const MAX_IMAGES = 4;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Keeps the images the server accepts, up to the limit; returns the rejected count too.
export const pickImages = (current, incoming) => {
  const accepted = [...current];
  let rejected = 0;
  [...incoming].forEach(file => {
    const ok =
      IMAGE_TYPES.includes(file.type) &&
      file.size <= MAX_IMAGE_BYTES &&
      accepted.length < MAX_IMAGES;
    if (ok) accepted.push(file);
    else rejected += 1;
  });
  return { accepted, rejected };
};

// What Zuwad AI is doing right now, in words support staff understand. Returns the key under
// ZUWAD_ASSISTANT.ACTIVITY for the running step (tool name plus its command preview).
const ACTIVITY_RULES = [
  [/vision|image/, 'IMAGE'],
  [/web_|browser/, 'WEB'],
  [/\b(sql|select|mysql|psql|wp_[a-z_]+|zuwad_ai_[a-z_]+)\b/i, 'DATABASE'],
  [/\b(logs?|journalctl|docker|systemctl|zuwad-server)\b/i, 'SERVERS'],
  [/search|\b(grep|rg|find)\b/i, 'SEARCH'],
  [/\b(read_file|cat|sed|head|tail|ls)\b/i, 'FILES'],
];
export const activityFor = step => {
  if (!step) return 'THINKING';
  const text = `${step.tool || ''} ${step.preview || ''}`;
  const match = ACTIVITY_RULES.find(([pattern]) => pattern.test(text));
  return match ? match[1] : 'WORKING';
};

// The chat list in sections: the viewer's own chats, then one section per team with a group per
// person, then (for supervisors) people without a team. A teammate in two shown teams appears in
// both. Threads keep their order (newest first).
export const groupThreads = ({
  threads,
  teams = [],
  myTeamIds = [],
  supervisor = false,
}) => {
  const mine = threads.filter(thread => thread.mine);
  const others = threads.filter(thread => !thread.mine);
  const shownTeams = supervisor
    ? teams
    : teams.filter(team => myTeamIds.includes(team.id));
  const peopleOf = list => {
    const byOwner = new Map();
    list.forEach(thread => {
      const owner = thread.owner || { id: 0, name: '' };
      if (!byOwner.has(owner.id)) byOwner.set(owner.id, { owner, threads: [] });
      byOwner.get(owner.id).threads.push(thread);
    });
    return [...byOwner.values()];
  };
  const sections = [{ key: 'mine', kind: 'mine', threads: mine }];
  shownTeams.forEach(team => {
    const list = others.filter(thread =>
      (thread.team_ids || []).includes(team.id)
    );
    if (list.length)
      sections.push({
        key: `team-${team.id}`,
        kind: 'team',
        name: team.name,
        count: list.length,
        people: peopleOf(list),
      });
  });
  const teamIds = new Set(shownTeams.map(team => team.id));
  const rest = others.filter(
    thread => !(thread.team_ids || []).some(id => teamIds.has(id))
  );
  if (rest.length)
    sections.push({
      key: 'no-team',
      kind: 'none',
      count: rest.length,
      people: peopleOf(rest),
    });
  return sections;
};
